import { Component, OnInit, inject } from "@angular/core";
import { TranslocoService, provideTranslocoScope } from "@jsverse/transloco";
import { UserAuthenticationService } from "@app/authentication/shared/user-authentication.service";
import { StandingDataType } from "@enums/StandingDataType";
import { TabMenuItem } from "@models/generic/TabModels/TabMenuItem";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { ToastrService } from "ngx-toastr";
import { Observable, combineLatest, firstValueFrom, map } from "rxjs";
import { Language } from "./shared/language.enum";
import { UserCurrentInfoModel } from "./shared/user-current-info-model";
import { UserService } from "./shared/user.service";

const USER_PROFILE_SCOPE = { scope: "user/user-profile" };

@Component({
    selector: "user-profile",
    templateUrl: "./user-profile.component.html",
    styleUrls: ["./user-profile.component.scss"],
    standalone: false,
    providers: [provideTranslocoScope({ scope: "user/user-profile", alias: "userProfile" })]
})
export class UserProfileComponent implements OnInit {
    private _ds = inject(UserService);
    private _authDS = inject(UserAuthenticationService);
    private _toastr = inject(ToastrService);
    private _transloco = inject(TranslocoService);

    tab: TabModel;
    user: UserCurrentInfoModel;
    passwordChangeModel = {
        oldPassword: "",
        newPassword: "",
        newPasswordConfirm: "",
    };

    ipConfigType;

    // Waits for the scope to load before emitting, so cerrix-select (which snapshots a
    // plain array once) never captures labels from before translations are ready.
    //
    // "Use tenant default" is keyed as 0, not null: ng-select's writeValue treats a null
    // bindValue as "not defined" and refuses to select it, leaving the field blank even
    // though the option exists. The Language enum has no 0 member, so 0 is a safe sentinel.
    // The template converts user.language (null when unset) to/from this sentinel.
    languageOptions$: Observable<{ ID: Language | 0; Name: string }[]> = combineLatest([
        this._transloco.selectTranslate("languageOptionUseTenantDefault", {}, USER_PROFILE_SCOPE),
        this._transloco.selectTranslate("languageOptionEnglish", {}, USER_PROFILE_SCOPE),
        this._transloco.selectTranslate("languageOptionDutch", {}, USER_PROFILE_SCOPE),
        this._transloco.selectTranslate("languageOptionNorwegian", {}, USER_PROFILE_SCOPE),
    ]).pipe(
        map(([useTenantDefault, english, dutch, norwegian]) => [
            { ID: 0, Name: useTenantDefault },
            { ID: Language.English, Name: english },
            { ID: Language.Dutch, Name: dutch },
            { ID: Language.Norwegian, Name: norwegian },
        ])
    );

    ngOnInit(): void {
        this._ds.getUserInfo().subscribe((x) => {
            this.user = x;

            if (this.user.manageIPAllowed) {
                this.ipConfigType = StandingDataType.IpWhitelistingPersonal;
            }

            this.tab.showLoader = false;
        });
    }

    // The dropdown shows user.language (null when unset) as the 0 sentinel; convert it back
    // here so the saved value and the resolveEffectiveLanguage fallback stay null-based.
    get languageSelectValue(): Language | 0 {
        return this.user.language ?? 0;
    }

    async languageChanged(newValue: Language | 0, languageUpdatedMessage: string) {
        const language = newValue || null;

        if (this.user.language === language) {
            return;
        }

        await firstValueFrom(this._ds.setLanguage(language));

        this.user.language = language;
        this._toastr.success(languageUpdatedMessage);

        // Per ADR 010: a language change re-bootstraps the whole app, never a live re-render.
        this.reloadPage();
    }

    // Extracted so specs can spy on it: window.location.reload is not spyable in place in
    // every test environment (its property is non-configurable).
    protected reloadPage(): void {
        window.location.reload();
    }

    logout() {
        this._authDS.logout().subscribe((x) => {
            window.location.reload();
        });
    }

    changePassword(passwordUpdatedMessage: string, successTitle: string, updateFailedTitle: string) {
        this._ds.changePassword(this.passwordChangeModel).subscribe((x) => {
            if (!x) {
                this.passwordChangeModel.oldPassword = "";
                this.passwordChangeModel.newPassword = "";
                this.passwordChangeModel.newPasswordConfirm = "";
                this._toastr.success(passwordUpdatedMessage, successTitle);
            } else {
                this._toastr.error(x, updateFailedTitle);
            }
        });
    }

    handleMenuItemClick(menuItem: TabMenuItem) {
        if (!menuItem) {
            return;
        }

        switch (menuItem.menuItemId) {
            case "roles":
                window.dispatchEvent(new Event("resize"));
                break;
        }
    }
}
